import React from 'react';
import { CheckCircleIcon, StarIcon, PackageIcon, TrendingUpIcon, LayersIcon } from 'lucide-react';
type PricingPlan = {
  name: string;
  price: string;
  period: string;
  subtitle: string;
  whoItsFor: string;
  features: string[];
  cta: string;
  recommended?: boolean;
  icon: React.ReactNode;
  href?: string;
};
export function PricingSection() {
  const plans: PricingPlan[] = [{
    name: 'Starter',
    price: '$297',
    period: '/month',
    subtitle: 'Ideal for small local businesses implementing lead management and scheduling tools.',
    whoItsFor: 'Best for solo operators or small teams running 1–2 ad channels.',
    icon: <PackageIcon className="w-7 h-7 text-graphite-gray" strokeWidth={1.5} data-id="element-123" />,
    features: ['A.I. Automated Lead Responses', 'Online Booking Calendars', 'Lead Tracking & Revenue Tools', '24/7 Lead-to-Booking Conversion'],
    cta: 'Get Started',
    href: 'https://buy.stripe.com/7sY7sM4gh69WcVo3vJdnW07'
  }, {
    name: 'Growth',
    price: '$497',
    period: '/month',
    subtitle: 'For businesses looking to scale with advanced CRM and online optimization.',
    whoItsFor: 'Best for growing companies with multiple crews and steady ad spend.',
    icon: <TrendingUpIcon className="w-7 h-7 text-viridia-green" strokeWidth={1.5} data-id="element-124" />,
    features: ['Advanced CRM Workflows', 'On-page SEO (GMB, Yelp)', 'Lead Follow-up Systems', 'QR Code & Meta Ad Design', 'Monthly Strategy Calls & Reporting'],
    cta: 'Get Growth Plan',
    recommended: true,
    href: 'https://buy.stripe.com/3cI00kcMN1TG9Jc0jxdnW0b'
  }, {
    name: 'Pro Scaling',
    price: '$1,497',
    period: '/month',
    subtitle: 'The ultimate centralized system for enterprise-level automation and marketing.',
    whoItsFor: 'Best for established companies running multi-channel campaigns and managing reputation at scale.',
    icon: <LayersIcon className="w-7 h-7 text-graphite-gray" strokeWidth={1.5} data-id="element-125" />,
    features: ['Multi-Channel Ad Management', 'Full Web Design & Optimization', 'Reputation Management Suite', 'Compliance & Review Monitoring', 'A/B Testing & Conversion Optimization'],
    cta: 'Get Pro Scaling',
    href: 'https://buy.stripe.com/3cIcN63cd69Wf3w8Q3dnW0c'
  }];
  return <section id="pricing" className="w-full bg-white-smoke py-16 md:py-24 px-4 relative" data-id="element-126">
      <div className="max-w-7xl mx-auto" data-id="element-127">
        <div className="text-center mb-6" data-id="element-128">
          <h2 className="text-3xl md:text-5xl font-extrabold text-jet-black mb-4" data-id="element-129">
            Small Business Automations & Workflow Packages
          </h2>
          <p className="text-lg md:text-xl text-graphite-gray mb-2" data-id="element-130">
            Flexible solutions designed for businesses at every stage of growth.
            All packages include dedicated account management and proven
            strategies.
          </p>
          <p className="text-base text-viridia-green font-semibold" data-id="element-131">
            Month-to-month. Cancel anytime. No setup fees.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mt-12" data-id="element-132">
          {plans.map((plan, index) => <div key={index} className={`bg-white rounded-xl p-8 flex flex-col relative transition-all duration-300 ${plan.recommended ? 'border-4 border-viridia-green shadow-xl shadow-viridia-green/10 md:-mt-4 md:mb-[-16px] hover:shadow-2xl hover:shadow-viridia-green/20' : 'border-2 border-gray-200 shadow-lg hover:border-viridia-green/50 hover:shadow-xl'}`} data-id="element-133">
              {plan.recommended && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2" data-id="element-134">
                  <div className="bg-viridia-green text-white px-5 py-2 rounded-full font-bold text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg whitespace-nowrap" data-id="element-135">
                    Recommended for Growth
                  </div>
                </div>}

              {/* Icon */}
              <div className="mb-5 mt-1" data-id="element-136">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${plan.recommended ? 'bg-viridia-green/10' : 'bg-gray-100'}`} data-id="element-137">
                  {plan.icon}
                </div>
              </div>

              {/* Plan name & subtitle */}
              <h3 className="text-2xl font-bold text-jet-black mb-2" data-id="element-138">
                {plan.name}
              </h3>
              <p className="text-sm text-graphite-gray mb-6 leading-relaxed" data-id="element-139">
                {plan.subtitle}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-3" data-id="element-140">
                <span className="text-4xl md:text-5xl font-extrabold text-jet-black" data-id="element-141">
                  {plan.price}
                </span>
                <span className="text-lg text-graphite-gray" data-id="element-142">
                  {plan.period}
                </span>
              </div>

              {/* Who it's for */}
              <p className="text-sm text-deep-teal font-semibold mb-8 leading-relaxed" data-id="element-143">
                {plan.whoItsFor}
              </p>

              {/* Divider */}
              <div className="border-t border-gray-200 mb-6" data-id="element-144" />

              {/* Features */}
              <ul className="space-y-4 mb-8 flex-1" data-id="element-145">
                {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start gap-3" data-id="element-146">
                    <CheckCircleIcon className="w-5 h-5 text-viridia-green flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-147" />
                    <span className="text-base text-graphite-gray" data-id="element-148">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              {plan.href ? <a href={plan.href} target="_blank" rel="noopener noreferrer" className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-200 text-center block ${plan.recommended ? 'bg-viridia-green text-white hover:bg-deep-teal hover:scale-105 hover:shadow-xl hover:shadow-viridia-green/20' : 'bg-white border-2 border-viridia-green text-viridia-green hover:bg-viridia-green hover:text-white hover:scale-105 hover:shadow-lg'}`} data-id="element-149">
                  {plan.cta}
                </a> : <button className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-200 ${plan.recommended ? 'bg-viridia-green text-white hover:bg-deep-teal hover:scale-105 hover:shadow-xl hover:shadow-viridia-green/20' : 'bg-white border-2 border-viridia-green text-viridia-green hover:bg-viridia-green hover:text-white hover:scale-105 hover:shadow-lg'}`} data-id="element-150">
                  {plan.cta}
                </button>}
            </div>)}
        </div>
      </div>
    </section>;
}